import type { ScheduledSpeech } from "../../../services/speech";
import {
  createDeliveryState,
  sayAgainEntry,
  transitionDelivery,
  type DeliveryAction,
  type DeliveryState,
} from "./model";

export interface DeliveryClock {
  wait(delayMs: number): Promise<void>;
}

export interface DeliverySpeaker {
  cancel(): void;
  pause(): void;
  resume(): void;
  speak(request: {
    readonly rate: number;
    readonly text: string;
  }): Promise<void>;
}

export interface DeliveryControllerOptions {
  readonly clock: DeliveryClock;
  readonly marker: () => Promise<void>;
  readonly onError: (error: unknown) => void;
  readonly onState: (state: DeliveryState) => void;
  readonly schedule: readonly ScheduledSpeech[];
  readonly speaker: DeliverySpeaker;
  readonly targetWpm: number;
}

export class DeliveryController {
  private generation = 0;
  private phase: "speaking" | "waiting" | null = null;
  private readonly initialTargetWpm: number;
  private stateValue: DeliveryState;

  constructor(private readonly options: DeliveryControllerOptions) {
    this.initialTargetWpm = options.targetWpm;
    this.stateValue = createDeliveryState(options.targetWpm);
    options.onState(this.stateValue);
  }

  state(): DeliveryState {
    return this.stateValue;
  }

  private update(action: DeliveryAction): void {
    this.stateValue = transitionDelivery(
      this.stateValue,
      action,
      this.options.schedule,
    );
    this.options.onState(this.stateValue);
  }

  play(): void {
    if (this.stateValue.status === "playing") return;
    if (this.stateValue.status === "paused" && this.phase === "speaking") {
      this.options.speaker.resume();
      this.update({ type: "PLAY" });
      return;
    }
    this.options.speaker.cancel();
    this.update({ type: "PLAY" });
    if (this.options.schedule.length === 0) return;
    const generation = ++this.generation;
    void this.run(generation);
  }

  pause(): void {
    if (this.stateValue.status !== "playing") return;
    this.options.speaker.pause();
    if (this.phase === "waiting") {
      this.generation += 1;
      this.phase = null;
    }
    this.update({ type: "PAUSE" });
  }

  stop(): void {
    this.generation += 1;
    this.phase = null;
    this.options.speaker.cancel();
    this.update({ type: "STOP" });
  }

  seek(
    action: Extract<
      DeliveryAction,
      { readonly type: "JUMP" | "NEXT" | "PREVIOUS" }
    >,
  ): void {
    const wasPlaying = this.stateValue.status === "playing";
    this.generation += 1;
    this.phase = null;
    this.options.speaker.cancel();
    this.update(action);
    if (wasPlaying) this.play();
  }

  nudge(delta: number): void {
    this.update({ type: "NUDGE_RATE", delta });
  }

  async sayAgain(): Promise<void> {
    const entry = sayAgainEntry(this.options.schedule, this.stateValue);
    if (entry === null) return;
    const wasPlaying = this.stateValue.status === "playing";
    this.generation += 1;
    this.phase = null;
    this.options.speaker.cancel();
    if (wasPlaying) this.update({ type: "PAUSE" });
    try {
      await this.options.speaker.speak({
        text: entry.text,
        rate: entry.rate * (this.stateValue.targetWpm / this.initialTargetWpm),
      });
    } catch (error: unknown) {
      this.options.onError(error);
    } finally {
      if (wasPlaying) this.play();
    }
  }

  private async run(generation: number): Promise<void> {
    try {
      while (
        generation === this.generation &&
        this.stateValue.status === "playing"
      ) {
        const entry = this.options.schedule[this.stateValue.cursor];
        /* v8 ignore next -- the state machine never exposes an active cursor outside the schedule */
        if (entry === undefined) return;
        this.phase = "waiting";
        if (entry.gapBeforeMs > 0)
          await this.options.clock.wait(entry.gapBeforeMs);
        if (generation !== this.generation || this.state().status !== "playing")
          return;
        if (entry.markerBefore) await this.options.marker();
        if (generation !== this.generation || this.state().status !== "playing")
          return;
        this.phase = "speaking";
        await this.options.speaker.speak({
          text: entry.text,
          rate:
            entry.rate * (this.stateValue.targetWpm / this.initialTargetWpm),
        });
        if (generation !== this.generation) return;
        this.phase = "waiting";
        if (entry.gapAfterMs > 0)
          await this.options.clock.wait(entry.gapAfterMs);
        if (generation !== this.generation || this.state().status !== "playing")
          return;
        this.update({ type: "FINISH_ENTRY" });
      }
    } catch (error: unknown) {
      if (generation === this.generation) {
        this.stop();
        this.options.onError(error);
      }
    } finally {
      if (generation === this.generation) this.phase = null;
    }
  }
}
